/**
 * Calendar Side Drawer Component
 *
 * Left-sliding drawer containing calendar selection and menu options.
 * Uses react-native-reanimated for smooth slide animations.
 *
 * Follows single responsibility principle: manages drawer UI and navigation.
 */

import { useEffect } from 'react';
import {
  Modal,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';

export type CalendarViewMode = 'month' | 'week' | 'day';

type CalendarSideDrawerProps = {
  visible: boolean;
  onClose: () => void;
  calendars: DeviceCalendar[];
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  onManageCalendars: () => void;
  viewMode?: CalendarViewMode;
  onViewModeChange?: (mode: CalendarViewMode) => void;
  onCalendarSetup?: () => void;
  onFamilyMembers?: () => void;
};

const DRAWER_WIDTH = 320;
const ANIMATION_DURATION = 250;

/**
 * CalendarSideDrawer Component
 *
 * Displays calendar picker and menu options in a left-sliding drawer.
 * Includes backdrop dismissal and smooth animations.
 */
export function CalendarSideDrawer({
  visible,
  onClose,
  calendars,
  selectedIds,
  onChange,
  onManageCalendars,
  viewMode = 'month',
  onViewModeChange,
  onCalendarSetup,
  onFamilyMembers,
}: CalendarSideDrawerProps) {
  const colorScheme = useColorScheme();
  const { top: topInset, bottom: bottomInset } = useSafeAreaInsets();
  const palette = Colors[colorScheme ?? 'light'];

  const textColor = palette.text;
  const backgroundColor = palette.background;
  const borderColor = palette.border;
  const accentColor = palette.primary;

  // Animation values
  const translateX = useSharedValue(-DRAWER_WIDTH);
  const backdropOpacity = useSharedValue(0);

  // Animate drawer and backdrop when visibility changes
  useEffect(() => {
    if (visible) {
      translateX.value = withTiming(0, { duration: ANIMATION_DURATION });
      backdropOpacity.value = withTiming(1, { duration: ANIMATION_DURATION });
    } else {
      translateX.value = withTiming(-DRAWER_WIDTH, { duration: ANIMATION_DURATION });
      backdropOpacity.value = withTiming(0, { duration: ANIMATION_DURATION });
    }
  }, [visible, translateX, backdropOpacity]);

  // Animated styles
  const drawerAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }],
  }));

  const backdropAnimatedStyle = useAnimatedStyle(() => ({
    opacity: backdropOpacity.value,
  }));

  if (!visible) {
    return null;
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <View style={styles.container}>
        {/* Backdrop */}
        <Animated.View
          style={[styles.backdrop, backdropAnimatedStyle]}
        >
          <TouchableOpacity
            style={StyleSheet.absoluteFillObject}
            activeOpacity={1}
            onPress={onClose}
          />
        </Animated.View>

        {/* Drawer */}
        <Animated.View
          style={[
            styles.drawer,
            {
              backgroundColor,
              borderRightColor: borderColor,
              paddingTop: topInset,
            },
            drawerAnimatedStyle,
          ]}
        >
          {/* Header */}
          <View style={[styles.header, { borderBottomColor: borderColor }]}>
            <Text style={[styles.headerTitle, { color: textColor }]}>
              Calendars
            </Text>
            <TouchableOpacity
              style={styles.closeButton}
              onPress={onClose}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons name="close" size={24} color={textColor} />
            </TouchableOpacity>
          </View>

          {/* View Mode Toggle */}
          {onViewModeChange && (
            <View style={[styles.viewModeSection, { borderBottomColor: borderColor }]}>
              <Text style={[styles.sectionLabel, { color: palette.textMuted }]}>
                View
              </Text>
              <View style={styles.viewModeButtons}>
                <TouchableOpacity
                  style={[
                    styles.viewModeButton,
                    {
                      backgroundColor: viewMode === 'month' ? accentColor : 'transparent',
                      borderColor: viewMode === 'month' ? accentColor : borderColor,
                    },
                  ]}
                  onPress={() => onViewModeChange('month')}
                  activeOpacity={0.7}
                >
                  <Ionicons
                    name="calendar-outline"
                    size={18}
                    color={viewMode === 'month' ? '#FFFFFF' : textColor}
                  />
                  <Text
                    style={[
                      styles.viewModeButtonText,
                      {
                        color: viewMode === 'month' ? '#FFFFFF' : textColor,
                      },
                    ]}
                  >
                    Month
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[
                    styles.viewModeButton,
                    {
                      backgroundColor: viewMode === 'week' ? accentColor : 'transparent',
                      borderColor: viewMode === 'week' ? accentColor : borderColor,
                    },
                  ]}
                  onPress={() => onViewModeChange('week')}
                  activeOpacity={0.7}
                >
                  <Ionicons
                    name="list-outline"
                    size={18}
                    color={viewMode === 'week' ? '#FFFFFF' : textColor}
                  />
                  <Text
                    style={[
                      styles.viewModeButtonText,
                      {
                        color: viewMode === 'week' ? '#FFFFFF' : textColor,
                      },
                    ]}
                  >
                    Week
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[
                    styles.viewModeButton,
                    {
                      backgroundColor: viewMode === 'day' ? accentColor : 'transparent',
                      borderColor: viewMode === 'day' ? accentColor : borderColor,
                    },
                  ]}
                  onPress={() => onViewModeChange('day')}
                  activeOpacity={0.7}
                >
                  <Ionicons
                    name="today-outline"
                    size={18}
                    color={viewMode === 'day' ? '#FFFFFF' : textColor}
                  />
                  <Text
                    style={[
                      styles.viewModeButtonText,
                      {
                        color: viewMode === 'day' ? '#FFFFFF' : textColor,
                      },
                    ]}
                  >
                    Day
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          )}

          {/* Menu Options */}
          <View style={[styles.menuContainer, { borderTopColor: borderColor, paddingBottom: bottomInset + 8 }]}>
            {onFamilyMembers && (
              <TouchableOpacity
                style={styles.menuItem}
                onPress={onFamilyMembers}
                activeOpacity={0.7}
              >
                <Ionicons name="people-outline" size={20} color={accentColor} />
                <Text style={[styles.menuItemText, { color: textColor }]}>
                  Family members
                </Text>
                <Ionicons name="chevron-forward" size={20} color={textColor} style={{ opacity: 0.5 }} />
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={styles.menuItem}
              onPress={onManageCalendars}
              activeOpacity={0.7}
            >
              <Ionicons name="settings-outline" size={20} color={accentColor} />
              <Text style={[styles.menuItemText, { color: textColor }]}>
                Filter calendars and labels
              </Text>
              <Ionicons name="chevron-forward" size={20} color={textColor} style={{ opacity: 0.5 }} />
            </TouchableOpacity>
            {onCalendarSetup && (
              <TouchableOpacity
                style={styles.menuItem}
                onPress={onCalendarSetup}
                activeOpacity={0.7}
              >
                <Ionicons name="calendar-outline" size={20} color={accentColor} />
                <Text style={[styles.menuItemText, { color: textColor }]}>
                  Calendar setup
                </Text>
                <Ionicons name="chevron-forward" size={20} color={textColor} style={{ opacity: 0.5 }} />
              </TouchableOpacity>
            )}
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  drawer: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: DRAWER_WIDTH,
    borderRightWidth: 1,
    shadowColor: '#000',
    shadowOpacity: 0.3,
    shadowOffset: { width: 2, height: 0 },
    shadowRadius: 8,
    elevation: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.lg,
    borderBottomWidth: 1,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  closeButton: {
    padding: Spacing.xs,
  },
  viewModeSection: {
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.lg,
    borderBottomWidth: 1,
  },
  sectionLabel: {
    fontSize: 10,
    fontWeight: '600',
    textTransform: 'uppercase',
    marginBottom: Spacing.md,
  },
  viewModeButtons: {
    flexDirection: 'row',
    gap: Spacing.sm,
  },
  viewModeButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: Spacing.sm + 2, // 10px
    paddingHorizontal: Spacing.md,
    borderRadius: BorderRadius.sm,
    borderWidth: 1,
    gap: Spacing.sm, // normalized from 6
  },
  viewModeButtonText: {
    fontSize: 12,
    fontWeight: '600',
  },
  menuContainer: {
    borderTopWidth: 1,
    paddingVertical: Spacing.sm,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md + 2, // 14px
    gap: Spacing.md,
  },
  menuItemText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
  },
});
